import { createWebSocket } from './websocket';
import {
  IncomingMessageSchema,
  type Message,
  type IncomingMessage,
  type SuperatomSDKConfig,
  type CollectionRegistry,
  type CollectionHandler,
  type CollectionOperation,
  type DatabaseType,
  type ModelStrategy,
  type DashCompModelConfig,
  Component,
  LLMProvider,
  Tool,
} from './types';
import { logger } from './utils/logger';
import { handleDataRequest } from './handlers/data-request';
import { handleBundleRequest } from './handlers/bundle-request';
import { handleAuthLoginRequest } from './handlers/auth-login-requests';
import { handleAuthVerifyRequest } from './handlers/auth-verify-request';
import { handleUserPromptRequest, handleUserPromptAbort } from './handlers/user-prompt-request';
import { handleUserPromptSuggestions } from './handlers/user-prompt-suggestions';
import { handleActionsRequest } from './handlers/actions-request';
import { handleComponentListResponse } from './handlers/components-list-response';
import { handleWorkflowListResponse } from './handlers/workflow-list-response';
import { handleUsersRequest } from './handlers/users';
import { handleDashboardsRequest } from './handlers/dashboards';
import { handleReportsRequest } from './handlers/reports';
import { handleUIsRequest } from './handlers/uis';
import { handleBookmarksRequest } from './handlers/bookmarks';
import { handleArtifactsRequest } from './handlers/artifacts';
import { handleKbNodesRequest } from './handlers/kb-nodes';
import { handleMenusRequest } from './handlers/menus';
import { handleDashCompRequest, dashboardConversationHistory } from './dashComp';
import { handleReportCompRequest } from './reportComp';
import { handleSchemaRequest } from './handlers/schema-request';
import { getLLMProviders, anthropicLLM, groqLLM, geminiLLM, openaiLLM } from './userResponse';
import { setUserManager, cleanupUserStorage } from './auth/user-storage';
import { UserManager } from './auth/user-manager';
import { setDashboardManager } from './dashboards/dashboard-storage';
import { DashboardManager } from './dashboards/dashboard-manager';
import { setReportManager } from './reports/report-storage';
import { ReportManager } from './reports/report-manager';
import { promptLoader } from './userResponse/prompt-loader';
import { queryCache } from './utils/query-cache';
import type { WorkflowDescriptor } from './userResponse/agents';


const DEFAULT_WS_URL = 'wss://ws.superatom.ai/websocket';

type MessageTypeHandler = (message: IncomingMessage) => void | Promise<void>;

export class SuperatomSDK {
  private ws: ReturnType<typeof createWebSocket> | null = null;
  private url: string;
  private apiKey?: string;
  private projectId: string;
  private type: string;
  private bundleDir: string | undefined;
  private messageHandlers: Map<string, (message: IncomingMessage) => void> = new Map();
  private messageTypeHandlers: Map<string, MessageTypeHandler> = new Map();
  private connected: boolean = false;
  private reconnectAttempts: number = 0;
  // Retry forever — the backend must self-heal across NAT timeouts, DO
  // redeploys, and long network outages. The previous cap (5) gave up after
  // ~30s and left the singleton SDK dead until process restart.
  private maxReconnectAttempts: number = Infinity;
  private collections: CollectionRegistry = {};
	private components: Component[] = [];
  private tools: Tool[] = [];
  private workflows: WorkflowDescriptor[] = [];
  private anthropicApiKey: string;
  private groqApiKey: string;
  private geminiApiKey: string;
  private openaiApiKey: string;
  private llmProviders: LLMProvider[];
  private databaseType: DatabaseType;
  private modelStrategy: ModelStrategy;
  private mainAgentModel: string;
  private sourceAgentModel: string;
  private dashCompModels?: DashCompModelConfig;
  private conversationSimilarityThreshold: number;
  private userManager: UserManager;
  private dashboardManager: DashboardManager;
  private reportManager: ReportManager;

  // Heartbeat properties for keeping WebSocket connection alive.
  // 25s ping + 10s grace stays under common NAT/LB idle thresholds (~60-100s)
  // so we detect dead sockets within seconds instead of minutes.
  private pingInterval: NodeJS.Timeout | null = null;
  private lastPong: number = Date.now();
  private readonly PING_INTERVAL_MS = 25000;
  private readonly PONG_TIMEOUT_MS = 35000;

  constructor(config: SuperatomSDKConfig) {
    // Set log level if provided in config (overrides environment variable)
    if (config.logLevel) {
      logger.setLogLevel(config.logLevel);
    }

    this.apiKey = config.apiKey;
    this.projectId = config.projectId;
    this.type = config.type || 'data-agent';
    this.bundleDir = config.bundleDir;
    this.url = config.url || process.env.SA_WEBSOCKET_URL || DEFAULT_WS_URL;
    this.anthropicApiKey = config.ANTHROPIC_API_KEY || process.env.ANTHROPIC_API_KEY || '';
    this.groqApiKey = config.GROQ_API_KEY || process.env.GROQ_API_KEY || '';
    this.geminiApiKey = config.GEMINI_API_KEY || process.env.GEMINI_API_KEY || '';
    this.openaiApiKey = config.OPENAI_API_KEY || process.env.OPENAI_API_KEY || '';
    this.llmProviders = config.LLM_PROVIDERS || getLLMProviders();
    this.databaseType = config.databaseType || 'postgresql';
    this.modelStrategy = config.modelStrategy || 'fast';
    this.mainAgentModel = config.mainAgentModel || 'anthropic/claude-sonnet-4-5-20250929';
    this.sourceAgentModel = config.sourceAgentModel || 'anthropic/claude-sonnet-4-5-20250929';
    this.dashCompModels = config.dashCompModels;
    this.conversationSimilarityThreshold = config.conversationSimilarityThreshold ?? 0.8;

    // Apply model strategy to all LLM providers
    this.applyModelStrategy(this.modelStrategy);

    // Apply conversation similarity threshold to all LLM providers
    this.applyConversationSimilarityThreshold(this.conversationSimilarityThreshold);

    // Configure query cache TTL (default: 5 minutes)
    if (config.queryCacheTTL !== undefined) {
      queryCache.setTTL(config.queryCacheTTL);
    }

    // Configure dashboard conversation history TTL (default: 30 minutes)
    if (config.dashboardHistoryTTL !== undefined) {
      dashboardConversationHistory.setTTL(config.dashboardHistoryTTL);
    }

    logger.info(`Initializing Superatom SDK for project ${this.projectId}, llm providers: ${this.llmProviders.join(', ')}, database type: ${this.databaseType}, model strategy: ${this.modelStrategy}, query cache TTL: ${queryCache.getTTL()} minutes`);

    // Initialize UserManager for this SDK instance
    this.userManager = new UserManager(this.projectId, 5000);

    // Initialize DashboardManager for this SDK instance
    this.dashboardManager = new DashboardManager(this.projectId);

    // Initialize ReportManager for this SDK instance
    this.reportManager = new ReportManager(this.projectId);

    // Initialize PromptLoader (load prompts into memory)
    this.initializePromptLoader(config.promptsDir).catch((error) => {
      logger.error('Failed to initialize PromptLoader:', error);
    });

    // Initialize UserManager with projectId (startup)
    this.initializeUserManager().catch((error) => {
      logger.error('Failed to initialize UserManager:', error);
    });

    // Initialize DashboardManager
    this.initializeDashboardManager();

    // Initialize ReportManager
    this.initializeReportManager();

    // Note: Connection is not automatic - call connect() explicitly when ready
  }

  /**
   * Initialize PromptLoader and load prompts into memory
   * Tries to load from file system first, then falls back to hardcoded prompts
   */
  private async initializePromptLoader(promptsDir?: string): Promise<void> {
    try {
      // Set custom prompts directory if provided
      if (promptsDir) {
        promptLoader.setPromptsDir(promptsDir);
      }

      // Set database type for SQL rules loading
      promptLoader.setDatabaseType(this.databaseType);

      await promptLoader.initialize();
      logger.info(`PromptLoader initialized with ${promptLoader.getCacheSize()} prompts from ${promptLoader.getPromptsDir()}, database type: ${this.databaseType}`);
    } catch (error) {
      logger.error('Failed to initialize PromptLoader:', error);
      throw error;
    }
  }

  /**
   * Initialize UserManager for the project
   */
  private async initializeUserManager(): Promise<void> {
    try {
      await this.userManager.init();
      // Set the global reference for backward compatibility with existing auth code
      setUserManager(this.userManager);
      logger.info(`UserManager initialized for project: ${this.projectId}`);
    } catch (error) {
      logger.error('Failed to initialize UserManager:', error);
      throw error;
    }
  }

  /**
   * Get the UserManager instance for this SDK
   */
  public getUserManager(): UserManager {
    return this.userManager;
  }

  /**
   * Initialize DashboardManager for the project
   */
  private initializeDashboardManager(): void {
    // Set the global reference for dashboard operations
    setDashboardManager(this.dashboardManager);
  }

  /**
   * Get the DashboardManager instance for this SDK
   */
  public getDashboardManager(): DashboardManager {
    return this.dashboardManager;
  }

  /**
   * Initialize ReportManager for the project
   */
  private initializeReportManager(): void {
    // Set the global reference for report operations
    setReportManager(this.reportManager);
  }

  /**
   * Get the ReportManager instance for this SDK
   */
  public getReportManager(): ReportManager {
    return this.reportManager;
  }

  /**
   * Connect to the Superatom WebSocket service
   */
  async connect(): Promise<void> {
    // If already connected, return immediately
    if (this.connected && this.ws && this.ws.readyState === this.ws.OPEN) {
      logger.info('Already connected to WebSocket');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        // Add all required query parameters
        const url = new URL(this.url);
        if (this.apiKey) {
          url.searchParams.set('apiKey', this.apiKey);
        }
        url.searchParams.set('projectId', this.projectId);
        url.searchParams.set('type', this.type);

        logger.info(`Connecting to WebSocket: ${url.toString()}`);

        this.ws = createWebSocket(url.toString());

        this.ws.addEventListener('open', () => {
          this.connected = true;
          this.reconnectAttempts = 0;
          logger.info('WebSocket connected successfully');
          this.startHeartbeat(); // Start heartbeat to keep connection alive
          resolve();
        });

        this.ws.addEventListener('message', (event: any) => {
          this.handleMessage(event.data);
        });

        this.ws.addEventListener('error', (error: any) => {
          logger.error('WebSocket error:', error);
          reject(error);
        });

        this.ws.addEventListener('close', () => {
          this.connected = false;
          logger.warn('WebSocket closed');
          this.handleReconnect();
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string): void {
    try {
      const parsed = JSON.parse(data);
      const message = IncomingMessageSchema.parse(parsed);

      logger.debug('Received message:', message.type);

      // Route message by type
      switch (message.type) {
        case 'PONG':
          // Handle heartbeat PONG response
          this.handlePong();
          break;

        case 'DATA_REQ':
          handleDataRequest(parsed, this.collections, (msg) => this.send(msg), this.tools).catch((error) => {
            logger.error('Failed to handle data request:', error);
          });
          break;

        case 'BUNDLE_REQ':
          handleBundleRequest(parsed, this.bundleDir, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle bundle request:', error);
          });
          break;

        case 'AUTH_LOGIN_REQ':
          handleAuthLoginRequest(parsed, this.collections, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle auth login request:', error);
          });
          break;

        case 'AUTH_VERIFY_REQ':
          handleAuthVerifyRequest(parsed, this.collections, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle auth verify request:', error);
          });
          break;

        case 'USER_PROMPT_REQ':
          handleUserPromptRequest(parsed, this.components, (msg) => this.send(msg), this.anthropicApiKey, this.groqApiKey, this.geminiApiKey, this.openaiApiKey, this.llmProviders, this.collections, this.tools, this.mainAgentModel, this.sourceAgentModel, this.workflows).catch((error) => {
            logger.error('Failed to handle user prompt request:', error);
          });
          break;

        case 'USER_PROMPT_ABORT_REQ':
          // Cancel an in-flight user-prompt request (user hit "Stop"). Synchronous,
          // fire-and-forget — aborts the controller and acks. Never throws.
          try {
            handleUserPromptAbort(parsed, (msg) => this.send(msg));
          } catch (error) {
            logger.error('Failed to handle user prompt abort request:', error);
          }
          break;

        case 'ACTIONS':
          handleActionsRequest(parsed, (msg) => this.send(msg), this.anthropicApiKey, this.groqApiKey, this.geminiApiKey, this.openaiApiKey, this.llmProviders).catch((error) => {
            logger.error('Failed to handle actions request:', error);
          });
          break;

        case 'USER_PROMPT_SUGGESTIONS_REQ':
          handleUserPromptSuggestions(parsed, this.components, (msg) => this.send(msg), this.collections).catch((error) => {
            logger.error('Failed to handle user prompt suggestions request:', error);
          });
          break;

        case 'COMPONENT_LIST_RES':
          handleComponentListResponse(parsed, (com) => this.storeComponents(com), this.collections).catch((error) => {
            logger.error('Failed to handle component list request:', error);
          });
          break;

        case 'WORKFLOW_LIST_RES':
          handleWorkflowListResponse(parsed, (wf) => this.setWorkflows(wf)).catch((error) => {
            logger.error('Failed to handle workflow list request:', error);
          });
          break;

        case 'USERS':
          handleUsersRequest(parsed, this.collections, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle users request:', error);
          });
          break;

        case 'DASHBOARDS':
          handleDashboardsRequest(parsed, this.collections, (msg: Message) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle dashboards request:', error);
          });
          break;

        case 'REPORTS':
          handleReportsRequest(parsed, this.collections, (msg: Message) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle reports request:', error);
          });
          break;

        case 'UIS':
          handleUIsRequest(parsed, this.collections, (msg: Message) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle UIs request:', error);
          });
          break;

        case 'BOOKMARKS':
          handleBookmarksRequest(parsed, this.collections, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle bookmarks request:', error);
          });
          break;

        case 'ARTIFACTS':
          handleArtifactsRequest(parsed, this.collections, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle artifacts request:', error);
          });
          break;

        case 'KB_NODES':
          handleKbNodesRequest(parsed, this.collections, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle KB nodes request:', error);
          });
          break;

        case 'MENUS':
          handleMenusRequest(parsed, this.collections, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle menus request:', error);
          });
          break;

        case 'DASH_COMP_REQ':
          handleDashCompRequest(parsed, this.components, (msg) => this.send(msg), this.anthropicApiKey, this.groqApiKey, this.geminiApiKey, this.openaiApiKey, this.llmProviders, this.collections, this.tools, this.dashCompModels).catch((error) => {
            logger.error('Failed to handle dash comp request:', error);
          });
          break;

        case 'REPORT_COMP_REQ':
          handleReportCompRequest(parsed, this.components, (msg) => this.send(msg), this.anthropicApiKey, this.groqApiKey, this.geminiApiKey, this.openaiApiKey, this.llmProviders, this.collections, this.tools, this.dashCompModels).catch((error) => {
            logger.error('Failed to handle report comp request:', error);
          });
          break;

        case 'SCHEMA_REQ':
          handleSchemaRequest(parsed, (msg) => this.send(msg)).catch((error) => {
            logger.error('Failed to handle schema request:', error);
          });
          break;

        default:
          // Check for custom message type handlers
          const handler = this.messageTypeHandlers.get(message.type);
          if (handler) {
            Promise.resolve(handler(message)).catch((error) => {
              logger.error(`Failed to handle ${message.type}:`, error);
            });
          }
          break;
      }

      // Call registered message handlers
      this.messageHandlers.forEach((handler) => {
        handler(message);
      });
    } catch (error) {
      logger.error('Failed to parse incoming message:', error);
    }
  }

  /**
   * Send a message to the Superatom service.
   * Returns true if the message was sent, false if the WebSocket is not connected.
   * Does NOT throw on closed connections — callers can check the return value if needed.
   */
  send(message: Message): boolean {
    if (!this.ws || !this.connected) {
      logger.warn('WebSocket is not connected, message not sent:', message.type);
      return false;
    }

    if (this.ws.readyState !== this.ws.OPEN) {
      logger.warn('WebSocket is not in OPEN state, message not sent:', message.type);
      return false;
    }

    try {
      const payload = JSON.stringify(message);
      this.ws.send(payload);
      return true;
    } catch (error) {
      logger.warn(`Failed to send WebSocket message (${message.type}):`, error instanceof Error ? error.message : String(error));
      return false;
    }
  }

  /**
   * Register a message handler to receive all messages
   */
  onMessage(handler: (message: IncomingMessage) => void): () => void {
    const id = Math.random().toString(36).substring(7);
    this.messageHandlers.set(id, handler);

    // Return unsubscribe function
    return () => {
      this.messageHandlers.delete(id);
    };
  }

  /**
   * Register a handler for a specific message type
   */
  onMessageType(type: string, handler: MessageTypeHandler): () => void {
    this.messageTypeHandlers.set(type, handler);

    // Return unsubscribe function
    return () => {
      this.messageTypeHandlers.delete(type);
    };
  }

  /**
   * Disconnect from the WebSocket service
   */
  disconnect(): void {
    this.stopHeartbeat(); // Stop heartbeat before disconnecting
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.connected = false;
    }
  }

  /**
   * Cleanup and disconnect - stops reconnection attempts and closes the connection
   */
  async destroy(): Promise<void> {
    // Stop heartbeat first
    this.stopHeartbeat();

    // Prevent further reconnection attempts
    this.maxReconnectAttempts = 0;
    this.reconnectAttempts = 0;

    // Clear all message handlers
    this.messageHandlers.clear();

    // Clear all collections
    this.collections = {};

    // Cleanup UserManager
    try {
      await cleanupUserStorage();
      logger.info('UserManager cleanup completed');
    } catch (error) {
      logger.error('Error during UserManager cleanup:', error);
    }

    // Disconnect
    this.disconnect();
  }

  /**
   * Check if the SDK is currently connected
   */
  isConnected(): boolean {
    return this.connected && this.ws !== null && this.ws.readyState === this.ws.OPEN;
  }

  /**
   * Register a collection handler for data operations
   */
  addCollection<TParams = any, TResult = any>(
    collectionName: string,
    operation: CollectionOperation | string,
    handler: CollectionHandler<TParams, TResult>
  ): void {
    if (!this.collections[collectionName]) {
      this.collections[collectionName] = {};
    }
    this.collections[collectionName][operation] = handler;
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      // Cap backoff at 30s so we don't sit idle for minutes after a long outage.
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      setTimeout(() => {
        logger.info(`Attempting to reconnect (attempt ${this.reconnectAttempts})...`);
        this.connect().catch((error) => {
          logger.error('Reconnection failed:', error);
        });
      }, delay);
    } else {
      logger.error('Max reconnection attempts reached');
    }
  }

  /**
   * Start heartbeat to keep WebSocket connection alive
   * Sends PING every 3 minutes to prevent idle timeout from cloud infrastructure
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing interval
    this.lastPong = Date.now();

    this.pingInterval = setInterval(() => {
      if (!this.ws || this.ws.readyState !== this.ws.OPEN) {
        logger.warn('WebSocket not open during heartbeat check, stopping heartbeat');
        this.stopHeartbeat();
        return;
      }

      // Check if we received a PONG within the timeout period
      const timeSinceLastPong = Date.now() - this.lastPong;
      if (timeSinceLastPong > this.PONG_TIMEOUT_MS) {
        logger.warn(`No PONG received for ${timeSinceLastPong}ms, connection may be dead. Reconnecting...`);
        this.stopHeartbeat();
        this.ws.close(); // This will trigger handleReconnect via the close event
        return;
      }

      // Send PING message
      try {
        const pingMessage: Message = {
          type: 'PING',
          from: { type: this.type },
          payload: { timestamp: Date.now() }
        };
        this.ws.send(JSON.stringify(pingMessage));
        logger.debug('Heartbeat PING sent');
      } catch (error) {
        logger.error('Failed to send PING:', error);
        this.stopHeartbeat();
        this.ws.close();
      }
    }, this.PING_INTERVAL_MS);

    logger.info(`Heartbeat started (PING every ${this.PING_INTERVAL_MS / 1000}s)`);
  }

  /**
   * Stop the heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
      logger.debug('Heartbeat stopped');
    }
  }

  /**
   * Handle PONG response from server
   */
  private handlePong(): void {
    this.lastPong = Date.now();
    logger.debug('Heartbeat PONG received');
  }

  private storeComponents(components: Component[]){
    this.components = components;
  }

  /**
   * Set tools for the SDK instance
   */
  public setTools(tools: Tool[]): void {
    this.tools = tools;
    logger.info(`Tools stored in SDK: ${tools.length} tools`);
  }

  /**
   * Get the stored tools
   */
  public getTools(): Tool[] {
    return this.tools;
  }

  /**
   * Register workflow components for the SDK instance.
   *
   * Workflows are pre-built multi-step UI flows the main agent can pick when
   * the user's prompt matches a workflow's `whenToUse` trigger. Picking a
   * workflow short-circuits analysis text + dashboard component generation —
   * the workflow component is returned directly, with the LLM-extracted props.
   */
  public setWorkflows(workflows: WorkflowDescriptor[]): void {
    this.workflows = workflows;
    logger.info(`Workflows stored in SDK: ${workflows.length} workflow(s)`);
  }

  /**
   * Get the registered workflow components.
   */
  public getWorkflows(): WorkflowDescriptor[] {
    return this.workflows;
  }

  /**
   * Apply model strategy to all LLM provider singletons
   * @param strategy - 'best', 'fast', or 'balanced'
   */
  private applyModelStrategy(strategy: ModelStrategy): void {
    anthropicLLM.setModelStrategy(strategy);
    groqLLM.setModelStrategy(strategy);
    geminiLLM.setModelStrategy(strategy);
    openaiLLM.setModelStrategy(strategy);
    logger.info(`Model strategy '${strategy}' applied to all LLM providers`);
  }

  /**
   * Set model strategy at runtime
   * @param strategy - 'best', 'fast', or 'balanced'
   */
  public setModelStrategy(strategy: ModelStrategy): void {
    this.modelStrategy = strategy;
    this.applyModelStrategy(strategy);
  }

  /**
   * Get current model strategy
   */
  public getModelStrategy(): ModelStrategy {
    return this.modelStrategy;
  }

  /**
   * Apply conversation similarity threshold to all LLM provider singletons
   * @param threshold - Value between 0 and 1 (e.g., 0.8 = 80% similarity required)
   */
  private applyConversationSimilarityThreshold(threshold: number): void {
    anthropicLLM.setConversationSimilarityThreshold(threshold);
    groqLLM.setConversationSimilarityThreshold(threshold);
    geminiLLM.setConversationSimilarityThreshold(threshold);
    openaiLLM.setConversationSimilarityThreshold(threshold);
    logger.info(`Conversation similarity threshold '${threshold}' applied to all LLM providers`);
  }

  /**
   * Set conversation similarity threshold at runtime
   * @param threshold - Value between 0 and 1 (e.g., 0.8 = 80% similarity required)
   */
  public setConversationSimilarityThreshold(threshold: number): void {
    this.conversationSimilarityThreshold = threshold;
    this.applyConversationSimilarityThreshold(threshold);
  }

  /**
   * Get current conversation similarity threshold
   */
  public getConversationSimilarityThreshold(): number {
    return this.conversationSimilarityThreshold;
  }

}

// Export types
export type { Message, IncomingMessage, SuperatomSDKConfig, CollectionHandler, CollectionOperation, User, UsersData, Tool, ToolOutputSchema, OutputField, DBUIBlock, KbNodesRequestPayload, KbNodesQueryFilters, DatabaseType, ModelStrategy } from './types';
export {LLM} from './llm';
export { UserManager } from './auth/user-manager';
export { UILogCollector, type CapturedLog } from './utils/log-collector';
export { Thread, UIBlock, ThreadManager, type Action } from './threads';
export { CleanupService } from './services/cleanup-service';
export { STORAGE_CONFIG } from './config/storage';
export { CONTEXT_CONFIG } from './config/context';
export { logger, type LogLevel } from './utils/logger';
// LLM Usage Logger for tracking API costs and performance
export { llmUsageLogger, type LLMUsageEntry } from './utils/llm-usage-logger';
// User Prompt Error Logger for capturing detailed error information
export { userPromptErrorLogger } from './utils/user-prompt-error-logger';
// BM25L Reranker for hybrid semantic search
export {
  BM25L,
  hybridRerank,
  rerankChromaResults,
  rerankConversationResults,
  type BM25LOptions,
  type HybridSearchOptions,
  type RerankedResult
} from './utils/bm25l-reranker';
// LLM Model Strategy for configuring model usage (ModelStrategy exported from types above)
export {
  type TaskType,
  type BaseLLMConfig,
  anthropicLLM,
  groqLLM,
  geminiLLM,
  openaiLLM
} from './userResponse';
// Query Cache for database query result caching
export { queryCache } from './utils/query-cache';
// Dashboard conversation history for per-dashboard scoped history
export { dashboardConversationHistory } from './dashComp';
// Multi-Agent system for alert analysis and programmatic use
export { MainAgent } from './userResponse/agents';
export {
  DEFAULT_AGENT_CONFIG,
  type AgentConfig,
  type AgentResponse,
  type WorkflowDescriptor,
  type SelectedWorkflow,
} from './userResponse/agents';
// Script flow — store, runner, and types. Exported so the backend can
// regenerate expired script datasets by re-running the saved recipe (#6).
export {
  ScriptStore,
  ScriptMatcher,
  runScript,
  normalizeScriptBody,
  resolveScriptRecipeStore,
} from './userResponse/scripts';
export type {
  ScriptRecipe,
  ScriptParameter,
  ScriptResult,
  ScriptComponentSpec,
  ScriptStoreOptions,
  ScriptRecipeStore,
  ScriptRecipeMetaRow,
} from './userResponse/scripts';